import { useEffect, useState, type FormEvent } from 'react'
import { AlertTriangle, Loader2, ShieldCheck, UtensilsCrossed } from 'lucide-react'
import * as api from '../api/auth'
import { ApiError } from '../api/auth'
import { TwoFactorQr } from '../components/TwoFactorQr'
import { PasswordField } from '../components/PasswordField'
import { useIsTouch } from '../hooks/useIsTouch'
import { useAuth } from '../context/AuthContext'
import { roleLabel } from '../lib/labels'
import { navigate, useRoute } from '../lib/router'
import type { TwoFactorSetup } from '../types/identity'

const INPUT =
  'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base sm:text-sm dark:border-white/10 dark:bg-white/5'
const BUTTON =
  'inline-flex w-full items-center justify-center gap-2 rounded-xl bg-salsa-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-salsa-600 disabled:opacity-60'
const LINK = 'w-full text-center text-xs font-medium text-slate-500 hover:text-salsa-600 dark:text-slate-400'

type Mode = 'login' | 'invite' | 'reset' | 'verify'

/** The public pages are ordinary URLs — the same ones the e-mails link to. */
function modeForPath(path: string): Mode {
  if (path === '/invite') return 'invite'
  if (path === '/reset-password') return 'reset'
  if (path === '/verify-email') return 'verify'
  return 'login'
}

export function LoginPage() {
  const touch = useIsTouch()
  const { signIn, completeTwoFactor, twoFactorRequired, user, markTwoFactorSatisfied, refresh } = useAuth()
  const route = useRoute()
  const mode = modeForPath(route.path)
  const token = route.query.get('token') ?? route.query.get('reset')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [code, setCode] = useState('')
  const [ticket, setTicket] = useState<string | null>(null)
  const [forgot, setForgot] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [violations, setViolations] = useState<string[]>([])
  const [notice, setNotice] = useState<string | null>(null)
  const [enrolment, setEnrolment] = useState<TwoFactorSetup | null>(null)
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [remember, setRemember] = useState(true)

  // `/verify-email?token=…` — the link an administrator's e-mail change sends.
  useEffect(() => {
    if (mode !== 'verify' || !token) return
    void guard(async () => {
      await api.verifyEmail(token)
      setNotice('E-mail address confirmed — you can sign in now.')
      navigate('/login', { replace: true })
    })
    // One-shot per token; `guard` only touches local state.
  }, [mode, token])

  function clear() {
    setError(null)
    setViolations([])
    setNotice(null)
  }

  async function guard(action: () => Promise<void>) {
    clear()
    setBusy(true)
    try {
      await action()
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.message)
        setViolations(caught.violations)
      } else {
        setError('Something went wrong')
      }
    } finally {
      setBusy(false)
    }
  }

  const handlers = {
    login: (event: FormEvent) => {
      event.preventDefault()
      void guard(async () => {
        // The “keep me signed in” choice goes with the password, so it also works for
        // people who have no authenticator to be asked about (GM, 2026-10-17).
        const result = await signIn(email, password, remember)
        if (result.mfaRequired && result.ticket) {
          setTicket(result.ticket)
          setNotice('Enter the 6-digit code from your authenticator app.')
        }
      })
    },
    twoFactor: (event: FormEvent) => {
      event.preventDefault()
      void guard(async () => {
        if (ticket) await completeTwoFactor(ticket, code, remember)
      })
    },
    invite: (event: FormEvent) => {
      event.preventDefault()
      void guard(async () => {
        if (!token) return
        if (newPassword !== confirmPassword) {
          throw new ApiError(422, 'password_mismatch', 'The passwords do not match.')
        }
        await api.acceptInvite(token, fullName, newPassword, remember)
        await refresh() // pick up the session the invitation just created
        navigate('/general-overview', { replace: true })
      })
    },
    reset: (event: FormEvent) => {
      event.preventDefault()
      void guard(async () => {
        if (!token) return
        await api.resetPassword(token, newPassword)
        setNotice('Password updated — you can sign in now.')
        navigate('/login', { replace: true })
      })
    },
    forgot: (event: FormEvent) => {
      event.preventDefault()
      void guard(async () => {
        await api.forgotPassword(email)
        setForgot(false)
        setNotice('If that address exists, a reset link is on its way.')
      })
    },
    startEnrolment: () =>
      void guard(async () => {
        setEnrolment(await api.startTwoFactorSetup())
      }),
    confirmEnrolment: (event: FormEvent) => {
      event.preventDefault()
      void guard(async () => {
        const result = await api.confirmTwoFactor(code)
        setRecoveryCodes(result.recovery_codes)
        await markTwoFactorSatisfied()
      })
    },
  }

  const step = twoFactorRequired
    ? 'enrol'
    : mode === 'invite'
      ? 'invite'
      : mode === 'reset'
        ? 'reset'
        : mode === 'verify'
          ? 'verify'
          : ticket
            ? 'twoFactor'
            : forgot
              ? 'forgot'
              : 'login'

  const titles: Record<string, string> = {
    enrol: 'Set up two-factor authentication',
    invite: 'Accept your invitation',
    reset: 'Choose a new password',
    verify: 'Confirm your e-mail address',
    twoFactor: 'Two-factor code',
    forgot: 'Reset your password',
    login: 'Sign in',
  }

  const onSubmit =
    step === 'enrol'
      ? handlers.confirmEnrolment
      : step === 'invite'
        ? handlers.invite
        : step === 'reset'
          ? handlers.reset
          : step === 'twoFactor'
            ? handlers.twoFactor
            : step === 'forgot'
              ? handlers.forgot
              : handlers.login

  const submitLabel =
    step === 'enrol'
      ? 'Confirm'
      : step === 'invite'
        ? 'Set password and sign in'
        : step === 'reset'
          ? 'Update password'
          : step === 'twoFactor'
            ? 'Verify'
            : step === 'forgot'
              ? 'Send reset link'
              : 'Sign in'

  // One flat list of inputs keeps the JSX small and readable.
  const inputs: Array<{
    id: string
    type: string
    placeholder: string
    value: string
    set: (v: string) => void
    minLength?: number
    autoFocus?: boolean
    /** Browser hint for password managers (D-107 kept this correct per field). */
    autoComplete?: string
    /** Names the show/hide toggle, so two password fields are never ambiguous. */
    revealLabel?: string
  }> = []
  if (step === 'invite') {
    inputs.push({ id: 'name', type: 'text', placeholder: 'Your full name', value: fullName, set: setFullName })
    inputs.push({
      id: 'pw',
      type: 'password',
      placeholder: 'Choose a password (12+ characters)',
      value: newPassword,
      set: setNewPassword,
      minLength: 12,
      autoComplete: 'new-password',
      revealLabel: 'new password',
    })
    inputs.push({
      id: 'pw-confirm',
      type: 'password',
      placeholder: 'Confirm password',
      value: confirmPassword,
      set: setConfirmPassword,
      minLength: 12,
      autoComplete: 'new-password',
      revealLabel: 'confirmation',
    })
  } else if (step === 'reset') {
    inputs.push({
      id: 'pw',
      type: 'password',
      placeholder: 'New password (12+ characters)',
      value: newPassword,
      set: setNewPassword,
      minLength: 12,
      autoComplete: 'new-password',
      revealLabel: 'new password',
    })
  } else if ((step === 'twoFactor' || step === 'enrol') && (step === 'twoFactor' || enrolment)) {
    inputs.push({
      id: 'code',
      type: 'text',
      placeholder: '123456',
      value: code,
      set: setCode,
      // On a phone the keyboard would open over the pairing QR and setup key, so
      // only grab focus on a desk (D-108).
      autoFocus: !touch,
    })
  } else if (step === 'forgot') {
    inputs.push({ id: 'email', type: 'email', placeholder: 'you@email.com', value: email, set: setEmail })
  } else if (step === 'login') {
    inputs.push({ id: 'email', type: 'email', placeholder: 'you@email.com', value: email, set: setEmail })
    inputs.push({ id: 'pw', type: 'password', placeholder: 'Your password', value: password, set: setPassword, autoComplete: 'current-password', revealLabel: 'password' })
  }

  const showSubmit =
    step !== 'verify' &&
    !(step === 'enrol' && !enrolment) &&
    !(step === 'enrol' && recoveryCodes.length > 0)

  return (
    // The invite/reset/2FA steps are taller than a phone viewport; a centred flex
    // box would clip the QR above the scroll origin, so top-align on phones (D-108).
    <div className="flex min-h-screen min-h-dvh items-start justify-center overflow-y-auto bg-slate-100 px-4 py-8 sm:items-center sm:py-10 dark:bg-ink-950">
      <div className="w-full max-w-md space-y-5">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-salsa-500 to-mango-500 text-white shadow-lg shadow-salsa-500/25">
            <UtensilsCrossed className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="leading-tight">
            <div className="flex items-center gap-2">
              <span className="text-base font-semibold tracking-tight text-slate-900 dark:text-white">
                Salsa Shop
              </span>
              <span className="badge bg-salsa-50 text-salsa-700 dark:bg-salsa-500/15 dark:text-salsa-300">
                Ops Hub
              </span>
            </div>
            <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
              Store Operations Platform
            </span>
          </div>
        </div>

        <form onSubmit={onSubmit} className="card space-y-4 p-5">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-salsa-500" aria-hidden="true" />
            <h1 className="text-sm font-semibold text-slate-900 dark:text-white">{titles[step]}</h1>
          </div>

          {user && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Signed in as {user.email} {roleLabel(user)}
            </p>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-xl border border-salsa-200 bg-salsa-50/70 px-3 py-2 text-xs text-salsa-800 dark:border-salsa-500/25 dark:bg-salsa-500/10 dark:text-salsa-200">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <div className="space-y-1">
                <p>{error}</p>
                {violations.length > 0 && (
                  <ul className="list-disc space-y-0.5 pl-4">
                    {violations.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {notice && !error && (
            <p className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-200">
              {notice}
            </p>
          )}

          {step === 'enrol' && !enrolment && (
            <>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Your role requires two-factor authentication. Pair an authenticator app to continue.
              </p>
              <button type="button" onClick={handlers.startEnrolment} className={BUTTON}>
                Start setup
              </button>
            </>
          )}

          {step === 'enrol' && enrolment && (
            <TwoFactorQr
              otpauthUri={enrolment.otpauth_uri}
              secret={enrolment.secret}
              account={enrolment.account}
            />
          )}

          {inputs.map((field) =>
            field.type === 'password' ? (
              // A password you cannot read is a password you mistype (D-107).
              <PasswordField
                key={field.id}
                value={field.value}
                placeholder={field.placeholder}
                aria-label={field.placeholder}
                onChange={(event) => field.set(event.target.value)}
                minLength={field.minLength}
                autoFocus={field.autoFocus}
                autoComplete={field.autoComplete}
                revealLabel={field.revealLabel}
                inputClassName={INPUT}
                required
              />
            ) : (
              <input
                key={field.id}
                type={field.type}
                value={field.value}
                placeholder={field.placeholder}
                aria-label={field.placeholder}
                onChange={(event) => field.set(event.target.value)}
                minLength={field.minLength}
                autoFocus={field.autoFocus}
                inputMode={field.id === 'code' ? 'numeric' : undefined}
                maxLength={field.id === 'code' ? 6 : undefined}
                autoComplete={field.id === 'code' ? 'one-time-code' : undefined}
                className={
                  field.id === 'code' ? `${INPUT} text-center font-mono text-lg tracking-[0.3em]` : INPUT
                }
                required
              />
            ),
          )}

          {(step === 'login' || step === 'twoFactor' || step === 'invite') && (
            <label
              htmlFor="remember-device"
              className="flex cursor-pointer items-start gap-2 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-white/5 dark:text-slate-300"
            >
              <input
                id="remember-device"
                type="checkbox"
                checked={remember}
                onChange={(event) => setRemember(event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-salsa-500"
              />
              <span>
                {step === 'login' ? (
                  <>
                    <span className="font-semibold">Keep me signed in for 30 days.</span> This
                    device stays signed in for a month — and skips the code prompt — so you do
                    not sign in every day. Untick on a shared or store tablet.
                  </>
                ) : (
                  <>
                    <span className="font-semibold">Remember this device for 30 days.</span> You
                    will not be asked for a code on this browser — untick on shared or store
                    tablets.
                  </>
                )}
              </span>
            </label>
          )}

          {recoveryCodes.length > 0 && (
            <ul className="grid grid-cols-2 gap-1 font-mono text-xs">
              {recoveryCodes.map((item) => (
                <li
                  key={item}
                  className="rounded-lg bg-slate-100 px-2 py-1 text-center text-slate-700 dark:bg-white/5 dark:text-slate-200"
                >
                  {item}
                </li>
              ))}
            </ul>
          )}

          {showSubmit && (
            <button type="submit" disabled={busy} className={BUTTON}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {submitLabel}
            </button>
          )}

          {recoveryCodes.length > 0 && (
            <button type="button" onClick={() => void markTwoFactorSatisfied()} className={BUTTON}>
              Continue to Store OS
            </button>
          )}

          {step === 'login' && (
            <button
              type="button"
              onClick={() => {
                clear()
                setForgot(true)
              }}
              className={LINK}
            >
              Forgot your password?
            </button>
          )}

          {step === 'forgot' && (
            <button
              type="button"
              onClick={() => {
                clear()
                setForgot(false)
              }}
              className={LINK}
            >
              Back to sign in
            </button>
          )}
        </form>

        <p className="text-center text-[11px] text-slate-400 dark:text-slate-500">
          Salsa Shop · Store OS — accounts are created by invitation only
        </p>
      </div>
    </div>
  )
}
